import { Link } from 'react-router-dom';
import { ArrowLeft, BarChart2 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ResultadoMateria } from '@condo/contracts';
import { useAsamblea, useResultados } from './api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

type ResultadoLabel = 'Aprobada' | 'Rechazada' | 'Sin quorum';
type ResultadoVariant = 'success' | 'danger' | 'warning';

const RESULTADO_CONFIG: Record<
  ResultadoMateria,
  { label: ResultadoLabel; variant: ResultadoVariant }
> = {
  aprobada: { label: 'Aprobada', variant: 'success' },
  rechazada: { label: 'Rechazada', variant: 'danger' },
  sin_quorum: { label: 'Sin quorum', variant: 'warning' },
};

const MAYORIA_LABELS: Record<string, string> = {
  simple: 'Mayoria simple (> 50%)',
  calificada_75: 'Calificada 75%',
  unanimidad: 'Unanimidad (100%)',
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  condoId: string;
  asambleaId: string;
}

/**
 * Resultados de votacion por materia.
 * No expone la relacion propietario→voto: solo muestra totales agregados.
 */
export function ResultadosAsamblea({ condoId, asambleaId }: Props) {
  const { data: asamblea, isLoading: loadingAsamblea } = useAsamblea(condoId, asambleaId);
  const { data: resultadosData, isLoading: loadingResultados } = useResultados(
    condoId,
    asambleaId,
  );

  const isLoading = loadingAsamblea || loadingResultados;

  if (isLoading) {
    return (
      <PageContainer width="wide">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full" />
        <Skeleton className="mt-4 h-64 w-full" />
      </PageContainer>
    );
  }

  if (!asamblea) return null;

  const resultados = resultadosData?.items ?? [];

  // Build lookup materiaId → resultado
  const resultadosByMateriaId = new Map(
    resultados.map((r) => [r.materiaId, r]),
  );

  const materiasVotables = asamblea.materias.filter(
    (m) => m.categoria !== 'informativa',
  );

  return (
    <PageContainer width="wide">
      <Button variant="ghost" asChild size="sm" className="mb-4">
        <Link to={`/app/condominios/${condoId}/asambleas/${asambleaId}`}>
          <ArrowLeft className="h-4 w-4" />
          Volver a la asamblea
        </Link>
      </Button>

      <PageHeader
        eyebrow="Asamblea · Resultados"
        title="Resultados de votacion"
        subtitle={`Asamblea ${asamblea.tipo} del ${new Date(asamblea.fechaProgramada).toLocaleDateString('es-CL')}`}
        ruleSpacing="mb-8"
      />

      {materiasVotables.length === 0 ? (
        <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-10 text-center">
          <BarChart2 className="mx-auto h-8 w-8 text-[var(--color-ink-muted)]" />
          <p className="mt-3 text-sm text-[var(--color-ink-muted)]">
            Esta asamblea no tuvo materias sujetas a votacion.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {materiasVotables.map((materia) => {
            const resultado = resultadosByMateriaId.get(materia.id);
            const config = resultado
              ? RESULTADO_CONFIG[resultado.resultado]
              : null;

            // Chart data: one bar per opcion
            const chartData = resultado
              ? Object.entries(resultado.conteoPorOpcion).map(([opcion, conteo]) => ({
                  opcion:
                    opcion === 'si'
                      ? 'Si'
                      : opcion === 'no'
                        ? 'No'
                        : opcion === 'abstencion'
                          ? 'Abstencion'
                          : opcion,
                  alicuota: Number((conteo.alicuotaAcumulada * 100).toFixed(2)),
                  votos: conteo.cantidadVotos,
                }))
              : [];

            return (
              <Card key={materia.id}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="eyebrow text-[0.6rem] mb-1">
                        Materia {String(materia.orden + 1).padStart(2, '0')}
                      </p>
                      <CardTitle className="text-base leading-snug">
                        {materia.titulo}
                      </CardTitle>
                    </div>
                    {config ? (
                      <Badge variant={config.variant} className="shrink-0">
                        {config.label}
                      </Badge>
                    ) : (
                      <Badge variant="neutral" className="shrink-0">
                        Sin resultados
                      </Badge>
                    )}
                  </div>
                </CardHeader>

                <CardContent>
                  {resultado && chartData.length > 0 ? (
                    <>
                      {/* Bar chart */}
                      <div className="h-52 w-full">
                        <ResponsiveContainer>
                          <BarChart data={chartData} barCategoryGap={16}>
                            <CartesianGrid
                              strokeDasharray="1 3"
                              stroke="var(--color-hairline)"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="opcion"
                              stroke="var(--color-ink-muted)"
                              tick={{ fontSize: 11 }}
                              tickLine={false}
                              axisLine={{ stroke: 'var(--color-hairline)' }}
                            />
                            <YAxis
                              stroke="var(--color-ink-muted)"
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v: number) => `${v}%`}
                              tickLine={false}
                              axisLine={false}
                              width={44}
                              domain={[0, 100]}
                            />
                            <Tooltip
                              cursor={{ fill: 'var(--color-paper-dim)' }}
                              contentStyle={{
                                background: 'var(--color-paper)',
                                border: '1px solid var(--color-hairline)',
                                borderRadius: 6,
                                fontSize: 12,
                              }}
                              formatter={(v: number, name: string) =>
                                name === 'alicuota' ? [`${v}%`, 'Alicuota'] : [v, 'Votos']
                              }
                            />
                            <Bar
                              dataKey="alicuota"
                              name="alicuota"
                              fill="var(--color-primary)"
                              radius={[3, 3, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Stats */}
                      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
                        <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-3">
                          <p className="eyebrow text-[0.6rem]">Alicuota participante</p>
                          <p className="mt-1 text-lg font-semibold tabular-nums">
                            {(resultado.alicuotaParticipante * 100).toFixed(1)}%
                          </p>
                        </div>
                        <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-3">
                          <p className="eyebrow text-[0.6rem]">Alicuota total condo</p>
                          <p className="mt-1 text-lg font-semibold tabular-nums">
                            {(resultado.alicuotaTotalCondominio * 100).toFixed(1)}%
                          </p>
                        </div>
                        <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-3">
                          <p className="eyebrow text-[0.6rem]">Mayoria requerida</p>
                          <p className="mt-1 text-sm font-semibold">
                            {MAYORIA_LABELS[resultado.mayoriaRequerida] ??
                              resultado.mayoriaRequerida}
                          </p>
                        </div>
                      </div>

                      {/* Per-opcion breakdown */}
                      <div className="mt-4">
                        <p className="mb-2 text-xs font-semibold text-[var(--color-ink-muted)] uppercase tracking-wide">
                          Detalle por opcion
                        </p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-[var(--color-hairline)] text-left text-xs text-[var(--color-ink-muted)]">
                                <th className="pb-2 pr-4 font-semibold">Opcion</th>
                                <th className="pb-2 pr-4 font-semibold text-right">Votos</th>
                                <th className="pb-2 font-semibold text-right">Alicuota</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--color-hairline)]">
                              {chartData.map((row) => (
                                <tr key={row.opcion}>
                                  <td className="py-2 pr-4 font-display font-semibold">
                                    {row.opcion}
                                  </td>
                                  <td className="py-2 pr-4 text-right tabular-nums">
                                    {row.votos}
                                  </td>
                                  <td className="py-2 text-right tabular-nums">
                                    {row.alicuota}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-[var(--color-ink-muted)]">
                      Aun no hay resultados computados para esta materia.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </PageContainer>
  );
}
